import React, { useEffect, useState, useRef } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import ParticleOverlay from './ParticleOverlay';
import CodeBlockAnimation from './CodeBlockAnimation';
import TypewriterPlaceholder from './TypewriterPlaceholder';
import { useTranslation } from 'react-i18next';

const SoftwareDevLoader = ({ onLoadingComplete }) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [currentPlaceholder, setCurrentPlaceholder] = useState('');
  const containerRef = useRef(null);
  const progressControls = useAnimation();
  const [isMounted, setIsMounted] = useState(false);
  
  // Set mounted state
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);
  
  // Get container dimensions for smoother animations
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  
  useEffect(() => {
    if (containerRef.current) {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    }
    
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Simulate loading progress with moderate increments
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        // Moderate progress increments
        const remaining = 100 - prev;
        const increment = Math.max(0.8, Math.min(remaining * 0.12, 3.5));
        const newProgress = prev + increment;
        
        if (newProgress >= 100) {
          clearInterval(interval);
          // Call the callback after a moderate delay
          setTimeout(() => {
            if (onLoadingComplete) onLoadingComplete();
          }, 600); // Moderate delay for transition
          return 100;
        }
        return parseFloat(newProgress.toFixed(1));
      });
    }, 90); // Moderate interval for animation
    
    return () => clearInterval(interval);
  }, [onLoadingComplete]);
  
  // Update progress bar animation - only after component is mounted
  useEffect(() => {
    if (isMounted) {
      progressControls.start({ 
        width: `${progress}%`, 
        transition: { duration: 0.3, ease: "easeInOut" } 
      });
    }
  }, [progress, progressControls, isMounted]);
  
  // Typewriter placeholders - balanced length for better readability
  const placeholders = [
    "Initializing environment...",
    "Loading modules...",
    "Compiling components...",
    "Optimizing performance...",
    "Connecting APIs...",
    "Preparing solutions..."
  ];

  return (
    <AnimatePresence>
      <motion.div 
        ref={containerRef}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Background animations - balanced opacity */}
        <ParticleOverlay className="opacity-35" />
        <div className="absolute inset-0 opacity-15">
          <CodeBlockAnimation />
        </div>
        
        {/* Center content */}
        <div className="relative z-10 flex flex-col items-center justify-center p-8 text-center">
          {/* Company name with balanced animation */}
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="mb-7"
          >
            <h1 className="text-primary text-3xl font-bold">Professional Solutions</h1>
          </motion.div>
          
          {/* Loading text with balanced animation */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4, ease: "easeOut" }}
            className="mb-6 text-primary font-bold text-xl h-10"
          >
            <TypewriterPlaceholder 
              placeholders={placeholders} 
              currentPlaceholder={currentPlaceholder}
            />
          </motion.div>
          
          {/* Progress bar with balanced animation */}
          <div className="w-64 h-3 bg-gray-800 rounded-full overflow-hidden mb-4 p-0.5">
            <motion.div 
              className="h-full bg-primary rounded-full"
              initial={{ width: 0 }}
              animate={progressControls}
            />
          </div>
          
          {/* Progress percentage with balanced animation */}
          <motion.div 
            className="text-gray-400 text-sm font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            {progress.toFixed(0)}%
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SoftwareDevLoader;